import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A vertical violin plot is a data visualization combining a box plot and a kernel density
          plot. It displays the distribution of a dataset, with the width of the
          &rdquo;violin&rdquo; representing data density at each value. The box inside shows the
          interquartile range (IQR), and the line marks the median. Violin plots are useful for
          comparing the distribution of data across multiple categories.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">
          Vertical violin plots are versatile visualizations that combine the features of box plots
          and density plots to display data distributions. Here are three significant uses:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Detailed Distribution Insights:</strong> They provide a clear view of the full
            distribution of data, showcasing the density and variability along with medians and
            interquartile ranges.
          </li>
          <li>
            <strong>Comparing Groups:</strong> Vertical violin plots excel at comparing
            distributions across multiple categories, making it easier to observe patterns or
            differences between groups.
          </li>
          <li>
            <strong>Highlighting Data Skewness:</strong> Their shape vividly reveals skewness and
            multimodal distributions, which may be less apparent in simpler chart types.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Vertical violin plots are a powerful way to visualize the distribution of data across
          different categories, but they come with certain limitations:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Overlapping Data Representation:</strong> When there is a large number of
            categories or closely spaced data points, vertical violin plots can become congested.
            This overlapping can make it difficult to discern patterns and interpret the data
            accurately, especially if the categories have similar distributions.
          </li>
          <li>
            <strong>Complexity in Interpretation:</strong> The dual-axis nature of violin plots
            (combining a box plot with a density plot) can make them difficult for some users to
            interpret, especially those who are not familiar with this type of chart. Understanding
            the distribution, symmetry, and skewness may require more time and expertise.
          </li>
          <li>
            <strong>Inconsistent Scale for Different Categories:</strong> In a vertical violin plot,
            the area is typically scaled according to the density of the data. However, if the data
            set has vastly different group sizes or distributions, the plot might exaggerate or
            understate the differences between categories, leading to misleading conclusions.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          Color selection in vertical violin plots plays an important role in making the data
          visually appealing and easily interpretable. By using distinct colors for different
          categories or distributions, you can highlight variations in data across multiple groups.
          Lighter tones for the inner part of the violins help emphasize the density distribution,
          while darker or contrasting colors can be used to highlight key elements such as the
          median or quartiles. Thoughtful color choices ensure that the plot remains clear, visually
          balanced, and accessible to users.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['diverging']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Vertical violin plots provide detailed insights into data distributions, but they come
          with some notable challenges:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Interpretation Complexity:</strong> Understanding the dual-sided density
            representation can be confusing for users unfamiliar with violin plots, leading to
            potential misinterpretations of the data.
          </li>
          <li>
            <strong>Overlapping Data:</strong> In cases of multiple overlapping categories,
            distinguishing between individual distributions can become visually challenging,
            reducing clarity.
          </li>
          <li>
            <strong>Scale and Smoothing Issues:</strong> Choosing appropriate scales and smoothing
            parameters is critical; improper settings can either oversimplify or overcomplicate the
            data representation, affecting accuracy.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
